import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('grc_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only redirect to login for 401 on auth-critical endpoints
    // Don't redirect for settings endpoints to prevent accidental logout
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      // Only logout for core auth endpoints, not admin settings
      if (!url.includes('/admin/settings')) {
        localStorage.removeItem('grc_token');
        localStorage.removeItem('grc_user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  updateMe: (data) => api.put('/auth/me', data),
  updatePassword: (data) => api.put('/auth/password', data),
  // Email management
  getEmails: () => api.get('/auth/me/emails'),
  addEmail: (email) => api.post('/auth/me/emails', { email }),
  setPrimaryEmail: (email) => api.put('/auth/me/emails/primary', { email }),
  deleteEmail: (email) => api.delete(`/auth/me/emails/${encodeURIComponent(email)}`),
};

// Cart API
export const cartAPI = {
  get: () => api.get('/cart'),
  add: (data) => api.post('/cart/add', data),
  remove: (itemId, variant = 'live') => api.delete(`/cart/remove/${itemId}?variant=${variant}`),
  updateQuantity: (itemId, variant, quantity) => api.put('/cart/update-quantity', { item_id: itemId, variant, quantity }),
  clear: () => api.delete('/cart/clear'),
  applyPromo: (code) => api.post('/cart/apply-promo', { promo_code: code }),
  removePromo: () => api.delete('/cart/remove-promo'),
  getCount: () => api.get('/cart/count'),
  checkout: (data) => api.post('/cart/checkout', data),
  getOrder: (orderId) => api.get(`/cart/order/${orderId}`),
  getOrderConfirmation: (orderId) => api.get(`/cart/order/${orderId}/confirmation`),
  getActivePromo: () => api.get('/cart/active-promo'),
  verifyOrder: (orderId) => api.post(`/cart/order/${orderId}/verify`),
};

// Industries API
export const industriesAPI = {
  getAll: () => api.get('/industries'),
  getById: (id) => api.get(`/industries/${id}`),
  getHierarchy: () => api.get('/industries/hierarchy'),
  getFlat: () => api.get('/industries/flat'),
  getMain: () => api.get('/industries/main'),
  getAnalytics: () => api.get('/industries/analytics'),
};

// Speakers API
export const speakersAPI = {
  getAll: (params) => api.get('/speakers', { params }),
  getById: (id) => api.get(`/speakers/${id}`),
  getFeatured: () => api.get('/speakers', { params: { featured: true, limit: 6 } }),
  getDropdown: () => api.get('/speakers-dropdown'),
};

// Webinars API
export const webinarsAPI = {
  getAll: (params) => api.get('/webinars', { params }),
  getById: (id) => api.get(`/webinars/${id}`),
  getFeatured: (limit = 6) => api.get('/webinars/featured', { params: { limit } }),
  getUpcoming: (limit = 10) => api.get('/webinars/upcoming', { params: { limit } }),
  getBestsellers: (limit = 10) => api.get('/webinars/bestsellers', { params: { limit } }),
  getCounts: (params = {}) => api.get('/webinars/counts', { params }),
  getMaterials: (webinarId) => api.get(`/webinars/${webinarId}/materials`),
};

// Seminars API
export const seminarsAPI = {
  getAll: (params) => api.get('/seminars', { params }),
  getById: (id) => api.get(`/seminars/${id}`),
  getFeatured: (limit = 6) => api.get('/seminars/featured', { params: { limit } }),
  getUpcoming: (limit = 10) => api.get('/seminars/upcoming', { params: { limit } }),
  getBestsellers: (limit = 10) => api.get('/seminars/bestsellers', { params: { limit } }),
  submitOfflineRequest: (data) => api.post('/seminars/offline-request', data),
};

// Series API
export const seriesAPI = {
  // Webinar Series
  getWebinarSeries: (params) => api.get('/series/webinar', { params }),
  getWebinarSeriesById: (id) => api.get(`/series/webinar/${id}`),
  getWebinarSeriesFeatured: (limit = 6) => api.get('/series/webinar/featured', { params: { limit } }),
  // Seminar Series
  getSeminarSeries: (params) => api.get('/series/seminar', { params }),
  getSeminarSeriesById: (id) => api.get(`/series/seminar/${id}`),
  // Available items for series creation
  getAvailableWebinars: () => api.get('/series/available-webinars'),
  getAvailableSeminars: () => api.get('/series/available-seminars'),
};

// Registrations API
export const registrationsAPI = {
  create: (data) => api.post('/registrations', data),
  getMy: () => api.get('/registrations/my'),
};

// Certificates API
export const certificatesAPI = {
  getMy: () => api.get('/certificates/my'),
  getById: (id) => api.get(`/certificates/${id}`),
};

// Orders API
export const ordersAPI = {
  getMy: () => api.get('/orders/my'),
  getById: (id) => api.get(`/orders/${id}`),
  getMyTransactions: () => api.get('/orders/my/transactions'),
  getInvoice: (orderId) => api.get(`/orders/${orderId}/invoice`),
  downloadInvoicePdf: (orderId) => api.get(`/orders/${orderId}/invoice/pdf`, { responseType: 'blob' }),
  getOrderDetails: (orderId) => api.get(`/orders/${orderId}/details`),
};

// Checkout API
export const checkoutAPI = {
  createSession: (data) => api.post('/checkout/create-session', {
    ...data,
    origin_url: window.location.origin
  }),
  getStatus: (sessionId) => api.get(`/checkout/status/${sessionId}`),
  getOrderBySession: (sessionId) => api.get(`/checkout/order-by-session/${sessionId}`),
};

// Contact API
export const contactAPI = {
  submit: (data) => api.post('/contact', data),
};

// Stats API
export const statsAPI = {
  getPublic: () => api.get('/stats/public'),
};

// Admin Actions API
export const adminActionsAPI = {
  calculateBestsellers: () => api.post('/admin/calculate-bestsellers'),
};

// Articles API
export const articlesAPI = {
  getAll: (params) => api.get('/articles', { params }),
  getById: (slugOrId) => api.get(`/articles/${slugOrId}`),
  getTags: () => api.get('/articles/tags/all'),
};

// Pages API (CMS)
export const pagesAPI = {
  get: (slug) => api.get(`/pages/${slug}`),
  getAll: () => api.get('/pages/'),
  update: (slug, data) => api.put(`/pages/${slug}`, data),
  reset: (slug) => api.post(`/pages/reset/${slug}`),
};

// Gallery API
export const galleryAPI = {
  getAll: (category) => api.get('/gallery', { params: category ? { category } : {} }),
  add: (data) => api.post('/gallery', data),
  delete: (id) => api.delete(`/gallery/${id}`),
};

// Bundles API (public)
export const bundlesAPI = {
  list: (params) => api.get('/bundles', { params }),
  getBySlug: (slug) => api.get(`/bundles/${slug}`),
};

// Bundles Admin API
export const adminBundlesAPI = {
  list: (params) => api.get('/bundles/admin/list', { params }),
  get: (id) => api.get(`/bundles/admin/${id}`),
  create: (data) => api.post('/bundles/admin/create', data),
  update: (id, data) => api.put(`/bundles/admin/${id}`, data),
  remove: (id) => api.delete(`/bundles/admin/${id}`),
  lookupWebinars: (params) => api.get('/bundles/admin/lookup/webinars', { params }),
};

// Admin API
export const adminAPI = {
  // Dashboard Stats
  getDashboardStats: () => api.get('/admin/stats'),
  
  // Users
  getUsers: (params) => api.get('/admin/users', { params }),
  getUser: (id) => api.get(`/admin/users/${id}`),
  updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  
  // Speakers
  getSpeakers: (params) => api.get('/admin/speakers', { params }),
  createSpeaker: (data) => api.post('/admin/speakers', data),
  updateSpeaker: (id, data) => api.put(`/admin/speakers/${id}`, data),
  deleteSpeaker: (id) => api.delete(`/admin/speakers/${id}`),
  
  // Webinars
  getWebinars: (params) => api.get('/admin/webinars', { params }),
  createWebinar: (data) => api.post('/admin/webinars', data),
  updateWebinar: (id, data) => api.put(`/admin/webinars/${id}`, data),
  deleteWebinar: (id) => api.delete(`/admin/webinars/${id}`),
  
  // Seminars
  getSeminars: (params) => api.get('/seminars', { params }),
  createSeminar: (data) => api.post('/seminars', data),
  updateSeminar: (id, data) => api.put(`/seminars/${id}`, data),
  deleteSeminar: (id) => api.delete(`/seminars/${id}`),
  getOfflineRequests: (params) => api.get('/seminars/offline-requests/all', { params }),
  updateOfflineRequestStatus: (id, status) => api.put(`/seminars/offline-request/${id}/status?status=${status}`),
  
  // Webinar Series
  getWebinarSeries: (params) => api.get('/series/webinar', { params }),
  getWebinarSeriesById: (id) => api.get(`/series/webinar/${id}`),
  createWebinarSeries: (data) => api.post('/series/webinar', data),
  updateWebinarSeries: (id, data) => api.put(`/series/webinar/${id}`, data),
  deleteWebinarSeries: (id) => api.delete(`/series/webinar/${id}`),
  getAvailableWebinars: () => api.get('/series/available-webinars'),
  
  // Seminar Series
  getSeminarSeries: (params) => api.get('/series/seminar', { params }),
  getSeminarSeriesById: (id) => api.get(`/series/seminar/${id}`),
  createSeminarSeries: (data) => api.post('/series/seminar', data),
  updateSeminarSeries: (id, data) => api.put(`/series/seminar/${id}`, data),
  deleteSeminarSeries: (id) => api.delete(`/series/seminar/${id}`),
  getAvailableSeminars: () => api.get('/series/available-seminars'),
  
  // Industries
  getIndustries: () => api.get('/admin/industries'),
  createIndustry: (data) => api.post('/admin/industries', data),
  updateIndustry: (id, data) => api.put(`/admin/industries/${id}`, data),
  deleteIndustry: (id) => api.delete(`/admin/industries/${id}`),
  
  // Orders
  getOrders: (params) => api.get('/admin/orders', { params }),
  getOrder: (id) => api.get(`/admin/orders/${id}`),
  updateOrderStatus: (id, status) => api.put(`/admin/orders/${id}/status`, { status }),
  
  // Invoices
  getInvoices: (params) => api.get('/admin/invoices', { params }),
  getInvoice: (orderId) => api.get(`/admin/invoices/${orderId}`),
  getInvoiceByOrder: (orderId) => api.get(`/admin/invoices/by-order/${orderId}`),
  generateInvoice: (orderId) => api.post(`/admin/invoices/generate/${orderId}`),
  generateBulkInvoices: (params) => api.post('/admin/invoices/generate-bulk', null, { params }),
  downloadInvoicePdf: (orderId) => api.get(`/admin/invoices/${orderId}/pdf`, { responseType: 'blob' }),
  getInvoiceOrderDetails: (orderId) => api.get(`/admin/invoices/${orderId}/order-details`),
  sendInvoiceEmail: (orderId) => api.post(`/admin/invoices/${orderId}/send-email`),
  fixZeroTotalInvoices: () => api.post('/admin/invoices/fix-zero-totals'),
  
  // Sales Report
  getSalesReport: (params) => api.get('/admin/reports/sales', { params }),
  exportSalesReportCsv: (params) => api.get('/admin/reports/sales/export', { params, responseType: 'blob' }),
  
  // Webinar Materials
  uploadWebinarMaterial: (webinarId, formData) => {
    return api.post(`/admin/webinars/${webinarId}/materials`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteWebinarMaterial: (webinarId, materialId) => api.delete(`/admin/webinars/${webinarId}/materials/${materialId}`),
  
  get: (endpoint) => api.get(`/admin${endpoint}`),
  post: (endpoint, data) => api.post(`/admin${endpoint}`, data),
  
  // Payment Settings
  getPaymentSettings: () => api.get('/admin/settings/payment'),
  updatePaymentSettings: (data) => api.put('/admin/settings/payment', data),
  testPaymentConnection: () => api.post('/admin/settings/payment/test'),
  
  // Email Settings
  getEmailSettings: () => api.get('/admin/settings/email'),
  updateEmailSettings: (data) => api.put('/admin/settings/email', data),
  testEmailSettings: (data) => api.post('/admin/settings/email/test', data),
  
  // Company Settings
  getCompanySettings: () => api.get('/admin/settings/company'),
  updateCompanySettings: (data) => api.put('/admin/settings/company', data),
  
  // CRM - Leads
  getLeads: (params) => api.get('/admin/leads', { params }),
  getLead: (id) => api.get(`/admin/leads/${id}`),
  createLead: (data) => api.post('/admin/leads', data),
  updateLead: (id, data) => api.put(`/admin/leads/${id}`, data),
  deleteLead: (id) => api.delete(`/admin/leads/${id}`),
  addLeadActivity: (id, data) => api.post(`/admin/leads/${id}/activity`, data),
  convertLead: (id) => api.post(`/admin/leads/${id}/convert`),
  getLeadsStats: () => api.get('/admin/leads/stats/summary'),
  
  // Analytics
  getAnalyticsSummary: (params) => api.get('/admin/analytics/summary', { params }),
  getRevenueAnalytics: (params) => api.get('/admin/analytics/revenue', { params }),
  getRegistrationAnalytics: (params) => api.get('/admin/analytics/registrations', { params }),
  getUserAnalytics: (params) => api.get('/admin/analytics/users', { params }),
  getTopPerformers: () => api.get('/admin/analytics/top-performers'),
  
  // Generic get/post for custom endpoints
  get: (url, config) => api.get(url, config),
  post: (url, data, config) => api.post(url, data, config),
  put: (url, data, config) => api.put(url, data, config),
  delete: (url, config) => api.delete(url, config),
  
  // Articles Management
  getArticles: (params) => api.get('/articles/admin', { params }),
  getArticle: (id) => api.get(`/articles/admin/${id}`),
  createArticle: (data) => api.post('/articles', data),
  updateArticle: (id, data) => api.put(`/articles/${id}`, data),
  deleteArticle: (id) => api.delete(`/articles/${id}`),
  publishArticle: (id) => api.post(`/articles/${id}/publish`),
  unpublishArticle: (id) => api.post(`/articles/${id}/unpublish`),
  
  // Pages/CMS Management
  getPages: () => api.get('/pages/'),
  getPage: (slug) => api.get(`/pages/${slug}`),
  updatePage: (slug, data) => api.put(`/pages/${slug}`, data),
  resetPage: (slug) => api.post(`/pages/reset/${slug}`),
  
  // Gallery Management
  getGalleryImages: (params) => api.get('/gallery', { params }),
  getGalleryCategories: () => api.get('/gallery/categories'),
  getGalleryStats: () => api.get('/gallery/stats'),
  getGalleryIndustryImages: (industryId) => api.get(`/gallery/industry/${industryId}`),
  addGalleryImage: (data) => api.post('/gallery', data),
  updateGalleryImage: (id, data) => api.put(`/gallery/${id}`, data),
  deleteGalleryImage: (id) => api.delete(`/gallery/${id}`),
  generateGalleryImage: (data) => api.post('/gallery/generate', data),
  generateIndustryImages: (industryId, count) => api.post(`/gallery/generate-industry-images/${industryId}?count=${count}`),
  migrateGalleryImages: () => api.post('/gallery/migrate-existing'),
  
  // R2 Storage APIs
  getR2Status: () => api.get('/storage/status'),
  uploadToR2: (folder, formData) => api.post(`/storage/upload-multiple/${folder}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  uploadSingleToR2: (folder, formData) => api.post(`/storage/upload/${folder}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  listR2Files: (folder) => api.get(`/storage/files/${folder}`),
  deleteR2File: (path) => api.delete(`/storage/files/${path}`),
  migrateSpeakersToR2: () => api.post('/storage/migrate-speakers'),
};

export default api;
